import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://junk4journals.com";

const forumCategories = [
  { id: 1, title: "Show & Tell", description: "Share your latest junk journal spreads, snail mail creations, and finished projects with the community.", icon: "ri-image-line", color: "bg-amber-50", accent: "text-amber-700" },
  { id: 2, title: "Pen Pal Matching", description: "Find your perfect pen pal! Post your interests, swap addresses, and start beautiful snail mail friendships.", icon: "ri-mail-send-line", color: "bg-rose-50", accent: "text-rose-700" },
  { id: 3, title: "Techniques & Tutorials", description: "Learn new skills, share tutorials, and ask questions about junk journaling and paper crafting techniques.", icon: "ri-lightbulb-line", color: "bg-green-50", accent: "text-green-700" },
  { id: 4, title: "Creator Spotlights", description: "Discuss your favorite creators, leave reviews, and help others discover amazing makers in the community.", icon: "ri-star-smile-line", color: "bg-[#f5ede0]", accent: "text-[#8b5e3c]" },
  { id: 5, title: "Swaps & Trades", description: "Organize paper swaps, ephemera trades, and collaborative mail art projects with fellow enthusiasts.", icon: "ri-exchange-line", color: "bg-orange-50", accent: "text-orange-700" },
  { id: 6, title: "Marketplace Talk", description: "Discuss products, ask for recommendations, and share deals from your favorite junk journal suppliers.", icon: "ri-shopping-bag-line", color: "bg-sky-50", accent: "text-sky-700" },
];

type Post = {
  id: number;
  title: string;
  author: string;
  forum: string;
  replies: number;
  time: string;
  body?: string;
};

export default function ForumsPage() {
  const [activeForumId, setActiveForumId] = useState<number | null>(null);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostBody, setNewPostBody] = useState("");
  const [posts, setPosts] = useState<Post[]>([]);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    document.title = "Community Forums — Junk Journal & Snail Mail Discussions | Junk & Post";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Join the Junk & Post community forums. Discuss junk journal techniques, snail mail tips, creator spotlights, and connect with thousands of paper crafting enthusiasts.");
    setMeta("og:title", "Community Forums — Junk Journal & Snail Mail Discussions | Junk & Post", true);
    setMeta("og:description", "Join the Junk & Post community forums. Discuss junk journal techniques, snail mail tips, and connect with paper crafting enthusiasts.", true);
    setMeta("og:url", `${SITE_URL}/forums`, true);

    const existing = document.getElementById("schema-forums");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "schema-forums";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DiscussionForumPosting",
      "headline": "Community Forums — Junk Journal & Snail Mail Discussions",
      "name": "Community Forums — Junk Journal & Snail Mail",
      "description": "Join the Junk4Journals community forums. Discuss junk journal techniques, snail mail tips, creator spotlights, and connect with paper crafting enthusiasts.",
      "text": "Welcome to the Junk4Journals community forums — the gathering place for junk journal makers and snail mail enthusiasts. Browse categories including Show & Tell, Pen Pal Matching, Techniques & Tutorials, Creator Spotlights, Swaps & Trades, and Marketplace Talk. Share your latest junk journal spreads, find pen pals, learn new paper crafting techniques, and connect with thousands of fellow enthusiasts.",
      "url": `${SITE_URL}/forums`,
      "datePublished": "2024-01-01T00:00:00Z",
      "dateModified": new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": "Junk4Journals",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "Junk4Journals",
        "url": SITE_URL
      },
      "image": {
        "@type": "ImageObject",
        "url": "https://public.readdy.ai/ai/img_res/4249073442332454bd4f34173e61c80d.jpg",
        "width": 1200,
        "height": 630
      },
      "isPartOf": {
        "@type": "WebSite",
        "url": SITE_URL,
        "name": "Junk4Journals"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById("schema-forums")?.remove();
    };
  }, []);

  const activeForum = forumCategories.find((f) => f.id === activeForumId);
  const filteredPosts = activeForumId
    ? posts.filter((p) => p.forum === activeForum?.title)
    : posts;

  const handleNewPost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPostTitle.trim()) return;
    const post: Post = {
      id: Date.now(),
      title: newPostTitle,
      author: "You",
      forum: activeForum?.title || "General",
      replies: 0,
      time: "Just now",
      body: newPostBody,
    };
    setPosts([post, ...posts]);
    setNewPostTitle("");
    setNewPostBody("");
    setNewPostOpen(false);
    setSuccessMsg("Your post has been shared with the community!");
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-10 bg-[#faf8f4] border-b border-[#ede5d8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs font-semibold uppercase tracking-widest mb-4">
                <i className="ri-discuss-line"></i>
                Community
              </div>
              <h1 className="text-4xl font-bold text-[#2c1e0f] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Community Forums
              </h1>
              <p className="text-[#7a6555]">Connect, share, and learn with junk journal and snail mail enthusiasts.</p>
            </div>
            <button
              onClick={() => setNewPostOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#8b5e3c] text-white text-sm font-semibold hover:bg-[#7a5234] transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </div>
              New Post
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {successMsg && (
          <div className="mb-6 px-5 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            {successMsg}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Forum Categories Sidebar */}
          <div className="lg:col-span-1">
            <h2 className="font-bold text-[#2c1e0f] mb-4 text-sm uppercase tracking-widest">
              Forum Categories
            </h2>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setActiveForumId(null)}
                className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${activeForumId === null ? "bg-[#8b5e3c] text-white" : "bg-white border border-[#ede5d8] text-[#3a2e1e] hover:border-[#c9a87c]"}`}
              >
                All Discussions
              </button>
              {forumCategories.map((forum) => (
                <button
                  key={forum.id}
                  onClick={() => setActiveForumId(forum.id)}
                  className={`text-left px-4 py-3 rounded-xl transition-colors cursor-pointer ${activeForumId === forum.id ? "bg-[#8b5e3c] text-white" : "bg-white border border-[#ede5d8] hover:border-[#c9a87c]"}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 flex items-center justify-center ${activeForumId === forum.id ? "text-white" : forum.accent}`}>
                      <i className={`${forum.icon} text-sm`}></i>
                    </div>
                    <span className={`text-sm font-medium ${activeForumId === forum.id ? "text-white" : "text-[#3a2e1e]"}`}>
                      {forum.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Posts List */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[#2c1e0f]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {activeForum ? activeForum.title : "All Discussions"}
              </h2>
              <span className="text-xs text-[#9a8a78]">{filteredPosts.length} post{filteredPosts.length !== 1 ? "s" : ""}</span>
            </div>

            {activeForum && (
              <div className={`p-4 rounded-xl ${activeForum.color} border border-[#ede5d8] mb-5`}>
                <p className={`text-sm ${activeForum.accent}`}>{activeForum.description}</p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {filteredPosts.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-[#ede5d8]">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#c9a87c]">
                    <i className="ri-chat-3-line text-3xl"></i>
                  </div>
                  <h3 className="font-bold text-[#2c1e0f] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    No posts yet
                  </h3>
                  <p className="text-sm text-[#7a6555] mb-4">Be the first to start a discussion!</p>
                  <button
                    onClick={() => setNewPostOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#8b5e3c] text-white text-sm font-semibold hover:bg-[#7a5234] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i> Start a Discussion
                  </button>
                </div>
              ) : (
                filteredPosts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white rounded-xl border border-[#ede5d8] p-4 hover:border-[#c9a87c]/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#2c1e0f] text-sm mb-1 leading-snug">{post.title}</h3>
                        {post.body && <p className="text-xs text-[#7a6555] mb-2 line-clamp-2">{post.body}</p>}
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[#9a8a78]">
                          <span className="text-[#8b5e3c] font-medium">{post.author}</span>
                          <span>in <strong className="text-[#6b5a4a]">{post.forum}</strong></span>
                          <span>{post.time}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-[#9a8a78] shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-chat-3-line"></i>
                        </div>
                        {post.replies}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Post Modal */}
      {newPostOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#2c1e0f] text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                Start a New Discussion
              </h3>
              <button
                onClick={() => setNewPostOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-[#9a8a78] hover:text-[#3a2e1e] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleNewPost} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">Post Title</label>
                <input
                  type="text"
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                  placeholder="What would you like to discuss?"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">Message</label>
                <textarea
                  value={newPostBody}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setNewPostBody(e.target.value);
                  }}
                  placeholder="Share your thoughts, questions, or ideas..."
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] resize-none"
                />
                <p className="text-xs text-[#9a8a78] mt-1 text-right">{newPostBody.length}/500</p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setNewPostOpen(false)}
                  className="px-5 py-2.5 rounded-full border border-[#ede5d8] text-sm text-[#7a6555] hover:bg-[#faf8f4] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-full bg-[#8b5e3c] text-white text-sm font-semibold hover:bg-[#7a5234] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Post Discussion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
